import * as React from 'react'
import { openApp } from 'src/helpers/appHelpers'
import { CtaModel } from '../types/ctaModel'

type ExecutorType = {
  handleSendFreeCard: () => void
  sponsorId?: string | number
}

function appendSponsorToUrl(
  inputUrl: string,
  sponsorId?: string | number,
): string {
  if (!inputUrl || sponsorId == null) return inputUrl
  const sId = String(sponsorId)

  try {
    const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(inputUrl)
    const base = hasProtocol ? undefined : window.location.origin
    const u = new URL(inputUrl, base)
    u.searchParams.set('sponsor', sId)

    if (!hasProtocol && inputUrl.startsWith('/')) {
      return `${u.pathname}${u.search}${u.hash}`
    }
    return u.toString()
  } catch {
    const [path, hash] = inputUrl.split('#')
    const sep = path.includes('?') ? '&' : '?'
    const withSponsor = `${path}${sep}sponsor=${encodeURIComponent(sId)}`
    return hash ? `${withSponsor}#${hash}` : withSponsor
  }
}

export const useCtaExecutor = ({
  handleSendFreeCard,
  sponsorId,
}: ExecutorType) => {
  const exec = React.useCallback(
    (cta?: CtaModel | null) => {
      if (!cta) return

      switch (cta.kind) {
        case 'external_url': {
          if (!cta.href) return
          const url = appendSponsorToUrl(cta.href, sponsorId)
          window.open(url, '_blank', 'noopener,noreferrer')
          return
        }

        case 'internal_url': {
          if (!cta.href) return
          const url = appendSponsorToUrl(cta.href, sponsorId)
          window.location.assign(url)
          return
        }

        case 'app_route': {
          const params: Record<string, any> = {
            ...(cta.params ?? {}),
            ...(sponsorId != null ? { sponsor: String(sponsorId) } : {}),
          }
          openApp(cta.route ?? '', params)
          return
        }

        case 'app_action': {
          switch (cta.action) {
            case 'SEND_FREE_CARD':
              handleSendFreeCard()
              return

            case 'OPEN_PRICING': {
              const url = appendSponsorToUrl('/app/pricing#packages', sponsorId)
              window.location.assign(url)
              return
            }

            case 'OPEN_CATALOG': {
              const params: Record<string, any> = {
                ...(cta.params ?? {}),
                ...(sponsorId != null ? { sponsor: String(sponsorId) } : {}),
              }
              openApp('/catalog', params)
              return
            }

            case 'OPEN_PROMPTINGS': {
              const url = appendSponsorToUrl(
                'https://www.promptings.com/',
                sponsorId,
              )
              window.open(url, '_blank', 'noopener,noreferrer')
              return
            }

            default:
              return
          }
        }
      }
    },
    [handleSendFreeCard, sponsorId],
  )

  return exec
}
